"use client";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import CardContribute from "./CardContribute";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import Link from "@mui/material/Link";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useSelect } from "@/context/ContextSelector";
import { useVirusOptions } from "@/context/virusSelector";

const ContributePage = () => {
  const theme = useTheme();
  const { contextValue } = useSelect();
  const virusOptions = useVirusOptions();
  const currentVirus =
    virusOptions.find((virus) => virus.value == contextValue)?.displayedValue ??
    "virus";

  return (
    <Box>
      <Typography
        sx={{
          fontFamily: "Roboto",
          fontSize: "18px",
          fontWeight: 400,
          marginLeft: "30px",
          marginTop: "30px",
          marginRight: "30px",
          color: theme.palette.grey[800],
        }}
      >
        This platform serves as the foundation for the{" "}
        <a href="https://lsdengue-arbogen.com/arbogen/">ARBOGEN project</a>.
        Initially aimed at identifying genetic determinants of the virus that
        play an important role in the severity of dengue disease, ARBOGEN&apos;s
        overarching goal is to foster arbovirus research through large scale
        virus genomic tracking by:
      </Typography>
      <List sx={{ paddingLeft: "30px" }}>
        {[
          "providing access to well-annotated, high-quality data",
          "offering tools for the exploration, sharing and analysis of virus genomic data",
        ].map((text, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              <FiberManualRecordIcon
                sx={{ fontSize: 8, color: theme.palette.grey[800] }}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "18px",
                    fontWeight: 400,
                    color: theme.palette.grey[800],
                  }}
                  variant="body1"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              }
            />
          </ListItem>
        ))}
      </List>
      <Typography
        sx={{
          fontFamily: "Roboto",
          fontSize: "18px",
          fontWeight: 400,
          marginLeft: "30px",
          marginTop: "30px",
          marginRight: "30px",
          color: theme.palette.grey[800],
        }}
      >
        We hope that this initiative will set standards &ndash; in terms of data
        use and commitment of data providers &ndash; that can help strengthen a
        framework for infectious disease genomics studies. More details on the
        project itself and its contributors are available on the{" "}
        <Link
          target="_blank"
          href="https://lsdengue-arbogen.com/arbogen/#project"
        >
          project website
        </Link>
        .
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          height: "275px",
          justifyContent: "space-between",
          margin: "30px",
          gap: "60px",
          marginTop: "60px",
        }}
      >
        <CardContribute
          link="/docs/Governance Data/Licenses_And_Rights"
          icon={
            <TextSnippetIcon
              sx={{ fontSize: "3rem", color: theme.palette.grey[100] }}
            />
          }
          title="Share with trust"
          description="Data governance and processing are led by rigourous standards."
          action="Discover the process"
        />
        <CardContribute
          link="/login"
          icon={
            <PersonAddIcon
              sx={{ fontSize: "3rem", color: theme.palette.grey[900] }}
            />
          }
          title="Join a community"
          description="ARBOGEN aims to facilitate collaboration on genomics for the arbovirus research community."
          action="Create an account"
        />
        <CardContribute
          link="/import"
          icon={
            <CloudUploadIcon
              sx={{ fontSize: "3rem", color: theme.palette.grey[800] }}
            />
          }
          title="Upload sequences"
          description="Data ara stored under a private, semi-private or public status."
          action="Upload a sequence"
        />
      </Box>
      <Typography
        sx={{
          fontFamily: "Roboto",
          fontSize: "30px",
          color: theme.palette.grey[800],
          fontWeight: 800,
          textAlign: "center",
          marginTop: "30px",
        }}
      >
        A large compendium of{" "}
        <span style={{ color: theme.palette.success.contrastText }}>
          {currentVirus}
        </span>{" "}
        genomes from across the globe
      </Typography>
      <Typography
        sx={{
          color: theme.palette.grey[100],
          fontFamily: "Roboto",
          fontSize: "18px",
          fontWeight: 400,
          textAlign: "center",
          marginTop: "10px",
        }}
      >
        The platform combines curated sequences based on public data from the
        NCBI as well as sequence data provided directly by users who wish to
        make their data available.
      </Typography>
      <Typography
        sx={{
          color: theme.palette.grey[100],
          fontFamily: "Roboto",
          fontSize: "18px",
          fontWeight: 400,
          textAlign: "center",
          marginTop: "10px",
        }}
      >
        Access to the sequence data itself is limited for sequences with private
        or semi-private status.
      </Typography>
    </Box>
  );
};

export default ContributePage;
